import type { AutobuildSessionThroughput, CdidLifecycleState, CdidSloStatus } from '@a7y/types';
import {
  fmtDuration,
  LIFECYCLE_STATE_LABELS as STATE_LABELS,
  LIFECYCLE_STATE_STYLES as STATE_STYLES,
} from './shared';

const SLO_STYLES: Record<CdidSloStatus, string> = {
  pass: 'border-emerald-300 bg-emerald-50/70 dark:border-emerald-800 dark:bg-emerald-950/20',
  fail: 'border-red-300 bg-red-50/70 dark:border-red-800 dark:bg-red-950/20',
  unknown: 'border-amber-300 bg-amber-50/70 dark:border-amber-800 dark:bg-amber-950/20',
  not_applicable: 'border-zinc-200 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-950/40',
};

function pct(value: number | null) {
  return value == null ? 'Not recorded' : `${value.toFixed(value % 1 === 0 ? 0 : 1)}%`;
}

export function SessionThroughputSection({
  throughput,
}: {
  throughput: AutobuildSessionThroughput;
}) {
  const stateCounts = new Map<CdidLifecycleState, number>();
  for (const bundle of throughput.bundles) {
    stateCounts.set(bundle.state, (stateCounts.get(bundle.state) ?? 0) + 1);
  }
  const degraded = throughput.outcome === 'completed_degraded';

  return (
    <section className="rounded-lg border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-100 px-3 py-2 dark:border-zinc-800">
        <span className="text-[11px] uppercase tracking-wide text-zinc-400">
          {throughput.schema === 'addis.lifecycle/v1' ? 'ADDIS throughput' : 'CDID throughput'}
        </span>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
            degraded
              ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
              : throughput.outcome === 'completed'
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                : 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300'
          }`}
        >
          {degraded
            ? 'Completed — degraded throughput'
            : throughput.outcome === 'completed'
              ? 'Completed'
              : 'Running'}
        </span>
      </div>

      <div className="grid gap-3 p-3 sm:grid-cols-3">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-zinc-400">
            Useful lane utilization
          </div>
          <div className="mt-0.5 text-lg font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">
            {pct(throughput.laneUtilizationPct)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-zinc-400">
            Longest idle + runnable
          </div>
          <div className="mt-0.5 text-lg font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">
            {fmtDuration(throughput.maxIdleRunnableMs / 1000)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-zinc-400">
            Landed / cost per landed
          </div>
          <div className="mt-0.5 text-lg font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">
            {throughput.landedTicketCount}
            <span className="ml-2 text-xs font-normal text-zinc-500">
              {throughput.costPerLandedTicketUsd == null
                ? 'cost unavailable'
                : `$${throughput.costPerLandedTicketUsd.toFixed(2)}`}
            </span>
          </div>
        </div>
      </div>

      {stateCounts.size > 0 && (
        <div className="flex flex-wrap gap-1.5 border-t border-zinc-100 px-3 py-2.5 dark:border-zinc-800">
          {[...stateCounts.entries()].map(([state, count]) => (
            <span
              key={state}
              className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATE_STYLES[state]}`}
            >
              {count} {STATE_LABELS[state]}
            </span>
          ))}
        </div>
      )}

      {throughput.schema === 'addis.lifecycle/v1' && throughput.sourceThroughput.length > 0 && (
        <div className="border-t border-zinc-100 px-3 py-2.5 dark:border-zinc-800">
          <div className="mb-2 text-[10px] uppercase tracking-wide text-zinc-400">
            Source throughput
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            {throughput.sourceThroughput.map((source) => (
              <div
                key={source.source}
                className="rounded-md bg-zinc-50 px-2.5 py-2 text-xs dark:bg-zinc-950/50"
              >
                <div className="font-semibold uppercase text-zinc-800 dark:text-zinc-200">
                  {source.source}
                </div>
                <div className="mt-0.5 text-zinc-500">
                  {source.landedTicketCount}/{source.ticketCount} landed ·{' '}
                  {fmtDuration(source.queueWaitMs / 1000)} queued
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {throughput.laneWindows.length > 0 && (
        <div className="border-t border-zinc-100 px-3 py-2.5 dark:border-zinc-800">
          <div className="mb-2 text-[10px] uppercase tracking-wide text-zinc-400">
            Provider lanes
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {throughput.laneWindows.map((lane) => (
              <div
                key={lane.laneId}
                className="rounded-md bg-zinc-50 px-2.5 py-2 text-xs dark:bg-zinc-950/50"
              >
                <div className="font-medium text-zinc-800 dark:text-zinc-200">
                  {lane.provider}:{lane.account}
                </div>
                <div className="mt-0.5 text-zinc-500">
                  {pct(lane.utilizationPct)} useful · {fmtDuration(lane.availableMs / 1000)}{' '}
                  available
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-2 border-t border-zinc-100 p-3 dark:border-zinc-800 sm:grid-cols-2">
        {throughput.slos.map((slo) => (
          <div key={slo.key} className={`rounded-md border px-3 py-2 ${SLO_STYLES[slo.status]}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-zinc-800 dark:text-zinc-200">
                {slo.target}
              </span>
              <span className="text-[10px] font-semibold uppercase text-zinc-500">
                {slo.status.replace('_', ' ')}
              </span>
            </div>
            <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">{slo.detail}</p>
          </div>
        ))}
      </div>

      {throughput.stop && (
        <div className="border-t border-zinc-100 px-3 py-2 text-xs text-zinc-500 dark:border-zinc-800">
          Stop:{' '}
          <span className="font-mono text-zinc-700 dark:text-zinc-300">
            {throughput.stop.reason}
          </span>
          {' · '}remaining {fmtDuration(throughput.stop.remainingMs / 1000)}
          {' · '}required reserve {fmtDuration(throughput.stop.requiredReserveMs / 1000)}
          {throughput.stop.capacity ? ` · ${throughput.stop.capacity.kind.replace('_', ' ')}` : ''}
        </div>
      )}
    </section>
  );
}
